import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import {
  Card, 
  Row, 
  Col, 
  Table, 
  Button, 
  Badge, 
  Form, 
  Spinner,
  Alert,
  Modal,
  InputGroup,
  FormControl,
  Pagination
} from 'react-bootstrap';
import { FaUserPlus, FaArrowRight, FaCheck, FaTimes, FaExclamationTriangle, FaPrint, FaStepForward, FaFileMedical, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const PatientQueue = () => {
  const { user } = useContext(AuthContext);
  const [queue, setQueue] = useState([]);
  const [filteredQueue, setFilteredQueue] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addFormData, setAddFormData] = useState({
    patientId: '',
    doctorId: '',
    appointmentId: '',
    notes: '',
    isEmergency: false
  });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [patientToSkip, setPatientToSkip] = useState(null);
  const [skipPositions, setSkipPositions] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  // Get permissions
  const permissions = user?.role === 'admin' ? {} : (user?.permissions || {});
  const isAdmin = user?.role === 'admin';
  const hasPermission = (key) => isAdmin || permissions[key] === true;

  // Load queue data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get doctors
        const doctorsRes = await axios.get('/api/doctors.php?active=1');
        
        if (doctorsRes.data && doctorsRes.data.length > 0) {
          setDoctors(doctorsRes.data);
          
          // If user is a doctor, set their ID as selected doctor
          if (user?.role === 'doctor' && user?.doctorId) {
            setSelectedDoctor(user.doctorId.toString());
          } else if (!selectedDoctor) {
            // If admin/staff and no doctor selected, select first one
            setSelectedDoctor(doctorsRes.data[0].id.toString());
          }
          
          // Get today's active queue for selected doctor
          if (selectedDoctor) {
            try {
              const queueRes = await axios.get(`/api/queue.php?doctorId=${selectedDoctor}&date=${selectedDate}`);
              console.log('Queue data:', queueRes.data);
              setQueue(queueRes.data || []);
              setFilteredQueue(queueRes.data || []);
              setError(null);
            } catch (queueErr) {
              console.error('Error fetching queue:', queueErr);
              setQueue([]);
              setFilteredQueue([]);
              setError('Failed to load patient queue. Please try again.');
            }
          }
        } else {
          setDoctors([]);
          setQueue([]);
          setFilteredQueue([]);
          setError('No active doctors found. Please add doctors to the system first.');
        }
      } catch (err) {
        console.error('Error fetching doctors:', err);
        setDoctors([]);
        setQueue([]);
        setFilteredQueue([]);
        setError('Failed to load doctors. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDoctor, selectedDate, user]);

  // Filter queue when search query changes
  useEffect(() => {
    if (!searchQuery) {
      setFilteredQueue(queue);
    } else {
      const filtered = queue.filter(item => {
        const patientName = item.Patient?.name?.toLowerCase() || '';
        const patientId = item.Patient?.patientId?.toLowerCase() || '';
        const notes = item.notes?.toLowerCase() || '';
        const searchLower = searchQuery.toLowerCase();
        
        return patientName.includes(searchLower) || 
               patientId.includes(searchLower) || 
               notes.includes(searchLower);
      });
      setFilteredQueue(filtered);
    }
    setCurrentPage(1);
  }, [searchQuery, queue]);

  // Handle doctor selection change
  const handleDoctorChange = (e) => {
    setSelectedDoctor(e.target.value);
  };

  // Handle date change
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  // Open add to queue modal
  const handleAddToQueue = () => {
    if (!selectedDoctor) {
      setError('Please select a doctor first.');
      return;
    }
    
    // Reset form data with selected doctor
    setAddFormData({
      patientId: '',
      doctorId: selectedDoctor,
      appointmentId: '',
      notes: '',
      isEmergency: false
    });
    
    // Load patients for selection
    const fetchPatients = async () => {
      try {
        const patientsRes = await axios.get('/api/patients');
        
        if (patientsRes.data && patientsRes.data.length > 0) {
          setPatients(patientsRes.data);
          
          // Load today's appointments for selected doctor
          try {
            const appsRes = await axios.get(`/api/appointments?date=${selectedDate}&doctorId=${selectedDoctor}`);
            const currentAppointments = appsRes.data || [];
            
            // Filter out appointments that are currently active in queue
            // Allow appointments that were no-show or cancelled to be re-added
            const filteredAppointments = currentAppointments.filter(app => {
              const queueEntry = queue.find(q => q.appointmentId === app.id);
              
              // If not in queue at all, allow it
              if (!queueEntry) {
                return ['scheduled', 'confirmed'].includes(app.status);
              }
              
              // If in queue but was no-show/cancelled, allow re-adding
              if (['no-show', 'cancelled'].includes(queueEntry.status)) {
                return true;
              }
              
              // If in queue and active (waiting/in-progress), don't allow
              return false;
            });
            
            setAppointments(filteredAppointments);
            setShowAddModal(true);
            setError(null);
          } catch (appErr) {
            console.error('Error fetching appointments:', appErr);
            setAppointments([]);
            setError('Failed to load appointments. Please try again.');
          }
        } else {
          setError('No patients found. Please add patients first.');
        }
      } catch (err) {
        console.error('Error fetching patients:', err);
        setError('Failed to load patient data. Please try again.');
      }
    };
    
    fetchPatients();
  };

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setAddFormData({
      ...addFormData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // If appointment is selected, auto-select the patient
    if (name === 'appointmentId' && value) {
      const appointment = appointments.find(app => app.id.toString() === value);
      if (appointment) {
        setAddFormData({
          ...addFormData,
          appointmentId: value,
          patientId: appointment.patientId.toString()
        });
      }
    }
  };

  // Add patient to queue
  const handleSubmitQueue = async (e) => {
    e.preventDefault();
    
    try {
      const queueData = {
        ...addFormData,
        appointmentId: addFormData.appointmentId || null
      };
      
      const response = await axios.post('/api/queue.php', queueData);
      
      // Close modal and refresh queue
      setShowAddModal(false);
      const updatedQueue = [...queue, response.data];
      setQueue(updatedQueue);
      setFilteredQueue(updatedQueue);
      setError(null);
      
    } catch (err) {
      console.error('Error adding to queue:', err);
      setError(err.response?.data?.msg || 'Failed to add patient to queue.');
    }
  };

  // Announce token function
  const announceToken = (queueItem) => {
    if (!('speechSynthesis' in window)) {
      console.error('Speech synthesis not supported');
      return;
    }
    
    window.speechSynthesis.cancel();
    
    const doctor = doctors.find(d => d.id === queueItem.doctorId);
    const doctorName = doctor ? doctor.name : '';
    const tokenNumber = queueItem.queueNumber;
    const patientName = queueItem.Patient?.name || '';
    
    const speakAnnouncement = () => {
      const voices = window.speechSynthesis.getVoices();
      
      // Filter female voices - exclude male names
      const femaleVoices = voices.filter(v => 
        !v.name.toLowerCase().includes('david') && 
        !v.name.toLowerCase().includes('mark') && 
        !v.name.toLowerCase().includes('george') &&
        !v.name.toLowerCase().includes('male') &&
        !v.name.toLowerCase().includes('james') &&
        !v.name.toLowerCase().includes('rishi')
      );
      
      // Priority: Indian English > Hindi > any female voice
      const femaleVoice = femaleVoices.find(v => v.lang === 'en-IN') || 
                          femaleVoices.find(v => v.lang.startsWith('hi')) ||
                          femaleVoices.find(v => v.name.includes('Heera')) ||
                          femaleVoices.find(v => v.name.includes('Swara')) ||
                          femaleVoices[0];
      
      console.log('All voices:', voices.map(v => v.name));
      console.log('Female voices:', femaleVoices.map(v => v.name));
      console.log('Selected:', femaleVoice?.name);
      
      const englishText = `Token number ${tokenNumber}. ${patientName}. Please proceed to Doctor ${doctorName}.`;
        
      const utterance1 = new SpeechSynthesisUtterance(englishText);
      if (femaleVoice) utterance1.voice = femaleVoice;
      utterance1.lang = femaleVoice?.lang === 'en-IN' ? 'en-IN' : 'en-US';
      utterance1.rate = 0.75;
      utterance1.pitch = 1.0;
      utterance1.volume = 0.9;
      
      utterance1.onend = () => {
        setTimeout(() => {
          const repeat1 = new SpeechSynthesisUtterance(englishText);
          if (femaleVoice) repeat1.voice = femaleVoice;
          repeat1.lang = femaleVoice?.lang === 'en-IN' ? 'en-IN' : 'en-US';
          repeat1.rate = 0.75;
          repeat1.pitch = 1.0;
          repeat1.volume = 0.9;
          window.speechSynthesis.speak(repeat1);
        }, 1000);
      };
      
      window.speechSynthesis.speak(utterance1);
    };
    
    // Load voices first
    if (window.speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.onvoiceschanged = () => {
        speakAnnouncement();
      };
    } else {
      speakAnnouncement();
    }
  };

  // Update queue status
  const updateQueueStatus = async (id, status) => {
    try {
      const response = await axios.put('/api/queue.php', { id, status });
      
      // Find the updated item from current queue
      const updatedItem = queue.find(item => item.id === id);
      if (updatedItem) {
        updatedItem.status = status;
        
        // Update queue item in state
        const updatedQueue = queue.map(item => item.id === id ? {...item, status} : item);
        setQueue(updatedQueue);
        setFilteredQueue(updatedQueue.filter(item => {
          if (!searchQuery) return true;
          const patientName = item.Patient?.name?.toLowerCase() || '';
          const patientId = item.Patient?.patientId?.toLowerCase() || '';
          const notes = item.notes?.toLowerCase() || '';
          const searchLower = searchQuery.toLowerCase();
          return patientName.includes(searchLower) || patientId.includes(searchLower) || notes.includes(searchLower);
        }));
        
        // If status changed to 'in-progress', announce the token
        if (status === 'in-progress') {
          announceToken({...updatedItem, status});
        }
      }
      
      setError(null);
      
    } catch (err) {
      console.error('Error updating queue status:', err);
      setError(err.response?.data?.error || 'Failed to update queue status. Please try again.');
    }
  };

  // Open skip modal
  const handleSkipClick = (patient) => {
    setPatientToSkip(patient);
    setSkipPositions(1);
    setShowSkipModal(true);
  };

  // Skip patient - just change status temporarily
  const handleConfirmSkip = async () => {
    if (!patientToSkip) return;
    
    try {
      // Mark as skipped by updating notes
      await axios.put('/api/queue.php', {
        id: patientToSkip.id,
        notes: (patientToSkip.notes || '') + ' [SKIPPED]'
      });
      
      // Refresh queue
      const queueRes = await axios.get(`/api/queue.php?doctorId=${selectedDoctor}&date=${selectedDate}`);
      const updatedQueue = queueRes.data || [];
      setQueue(updatedQueue);
      setFilteredQueue(updatedQueue);
      setShowSkipModal(false);
      setError(null);
      
    } catch (err) {
      console.error('Error skipping patient:', err);
      setError(err.response?.data?.error || 'Failed to skip patient. Please try again.');
    }
  };

  // Format time from HH:MM:SS to 12-hour format
  const formatTime = (timeString) => {
    if (!timeString) return '';
    
    try {
      const time = new Date(`2000-01-01T${timeString}`);
      return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (err) {
      console.error('Error formatting time:', err);
      return timeString; // Return original if formatting fails
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'waiting':
        return <Badge bg="warning">Waiting</Badge>;
      case 'in-progress':
        return <Badge bg="primary">With Doctor</Badge>;
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      case 'cancelled':
        return <Badge bg="danger">Cancelled</Badge>;
      case 'no-show':
        return <Badge bg="secondary">No Show</Badge>;
      default:
        return <Badge bg="light" text="dark">{status}</Badge>;
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredQueue.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentQueueItems = filteredQueue.slice(startIndex, endIndex);

  return (
    <div>
      <h2 className="mb-4">Patient Queue</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Row className="mb-4">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Select Doctor</Form.Label>
            <Form.Select 
              value={selectedDoctor} 
              onChange={handleDoctorChange}
              disabled={loading || doctors.length === 0 || user?.role === 'doctor'}
            >
              {doctors.length === 0 ? (
                <option value="">No doctors available</option>
              ) : (
                doctors.map(doctor => (
                  <option key={doctor.id} value={doctor.id}>
                    Dr. {doctor.name} - {doctor.specialization}
                  </option>
                ))
              )}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              disabled={loading}
            />
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <Button 
            variant="primary" 
            className="mb-3 w-100"
            onClick={handleAddToQueue}
            disabled={loading || doctors.length === 0 || !selectedDoctor}
          >
            <FaUserPlus className="me-2" /> Add to Queue
          </Button>
        </Col>
      </Row>
      
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <FormControl
              placeholder="Search by patient name, ID or notes"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button variant="outline-secondary">
              <FaSearch />
            </Button>
          </InputGroup>
        </Col>
      </Row>
      
      <Card>
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0">Current Queue</h5>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading queue data...</p>
            </div>
          ) : doctors.length === 0 ? (
            <div className="text-center py-5">
              <p className="mb-0">No doctors available. Please add doctors to the system.</p>
            </div>
          ) : queue.length === 0 ? (
            <div className="text-center py-5">
              <p className="mb-0">No patients in queue.</p>
            </div>
          ) : filteredQueue.length === 0 ? (
            <div className="text-center py-5">
              <p className="mb-0">No patients found matching search criteria.</p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th width="5%">#</th>
                  <th width="30%">Patient</th>
                  <th width="20%">Appointment</th>
                  <th width="10%">Status</th>
                  <th width="10%">Wait Time</th>
                  <th width="25%">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentQueueItems.map(item => {
                  // Calculate wait time with timezone correction
                  const dateStr = item.registeredAt.replace(' ', 'T');
                  const registeredAt = new Date(dateStr);
                  registeredAt.setHours(registeredAt.getHours() + 5); // Add Pakistan timezone offset
                  
                  const now = new Date();
                  const diffMinutes = Math.floor((now - registeredAt) / (1000 * 60));
                  const hours = Math.floor(diffMinutes / 60);
                  const minutes = diffMinutes % 60;
                  const waitTime = hours > 0 
                    ? `${hours}h ${minutes}m` 
                    : `${minutes}m`;
                    
                  // Determine if wait time is excessive (more than 30 minutes)
                  const excessiveWait = item.status === 'waiting' && diffMinutes > 30;
                  
                  return (
                    <tr key={item.id} className={item.isEmergency === 1 ? 'table-danger' : ''}>
                      <td>
                        <span>{item.queueNumber}</span>
                        {item.isEmergency === 1 && (
                          <FaExclamationTriangle className="ms-2 text-danger" title="Emergency" />
                        )}
                      </td>
                      <td>
                        <div>{item.Patient?.name || 'Unknown Patient'}</div>
                        <small className="text-muted">
                          {item.Patient?.age} yrs, {item.Patient?.gender}
                          {item.notes && <div><em>Note: {item.notes}</em></div>}
                        </small>
                      </td>
                      <td>
                        {item.Appointment ? (
                          <div>
                            <div>{formatTime(item.Appointment.appointmentTime)}</div>
                            <Badge bg="info">{item.Appointment.appointmentType}</Badge>
                          </div>
                        ) : (
                          <Badge bg="warning">Walk-in</Badge>
                        )}
                      </td>
                      <td>{getStatusBadge(item.status)}</td>
                      <td className={excessiveWait ? 'text-danger fw-bold' : ''}>
                        {item.status === 'waiting' ? waitTime : '—'}
                      </td>
                      <td>
                        {hasPermission('printTicket') && (
                          <Button
                            variant="info"
                            size="sm"
                            className="me-2"
                            onClick={() => navigate(`/queue/ticket/${item.id}`)}
                          >
                            <FaPrint className="me-1" /> Ticket
                          </Button>
                        )}

                        {(['waiting', 'no-show'].includes(item.status) || (item.notes && item.notes.includes('[SKIPPED]'))) && (
                          <>
                            {hasPermission('callIn') && (
                              <Button
                                variant="primary"
                                size="sm"
                                className="me-2"
                                onClick={() => updateQueueStatus(item.id, 'in-progress')}
                              >
                                <FaArrowRight className="me-1" /> Call In
                              </Button>
                            )}
                            {item.status === 'waiting' && !item.notes?.includes('[SKIPPED]') && hasPermission('skipQueue') && (
                              <Button
                                variant="warning"
                                size="sm"
                                className="me-2"
                                onClick={() => handleSkipClick(item)}
                                title="Skip patient"
                              >
                                <FaStepForward className="me-1" /> Skip
                              </Button>
                            )}
                          </>
                        )}

                        {item.status === 'in-progress' && (
                          <>
                            <Button
                              variant="primary"
                              size="sm"
                              className="me-2"
                              onClick={() => navigate(`/reports/create/${item.Patient?.id || item.patientId}`)}
                            >
                              <FaFileMedical className="me-1" /> Create Report
                            </Button>
                            <Button
                              variant="success"
                              size="sm"
                              className="me-2"
                              onClick={async () => {
                                await updateQueueStatus(item.id, 'completed');
                                if (item.appointmentId) {
                                  try {
                                    await axios.put(`/api/appointments/${item.appointmentId}`, { status: 'completed' });
                                  } catch (err) {
                                    console.error('Error updating appointment:', err);
                                  }
                                }
                              }}
                            >
                              <FaCheck className="me-1" /> Complete
                            </Button>
                          </>
                        )}

                        {['waiting', 'in-progress'].includes(item.status) && hasPermission('noShow') && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={async () => {
                              await updateQueueStatus(item.id, 'no-show');
                              // Also update appointment status if appointment exists
                              if (item.appointmentId) {
                                try {
                                  await axios.put(`/api/appointments/${item.appointmentId}`, { status: 'no-show' });
                                } catch (err) {
                                  console.error('Error updating appointment status:', err);
                                }
                              }
                            }}
                          >
                            <FaTimes className="me-1" /> No Show
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          
          {/* Pagination */}
          {filteredQueue.length > itemsPerPage && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                
                {[...Array(totalPages)].map((_, index) => (
                  <Pagination.Item
                    key={index + 1}
                    active={index + 1 === currentPage}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </Pagination.Item>
                ))}
                
                <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Skip Patient Modal */}
      <Modal show={showSkipModal} onHide={() => setShowSkipModal(false)}>
        <Modal.Header>
          <Modal.Title>Skip Patient</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>Patient:</strong> {patientToSkip?.Patient?.name}
            <br />
            <strong>Current Position:</strong> #{patientToSkip?.queueNumber}
          </p>
          
          <Form.Group>
            <Form.Label>Skip by how many positions? *</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={skipPositions}
              onChange={(e) => setSkipPositions(e.target.value)}
              placeholder="Enter number of positions"
            />
            <Form.Text className="text-muted">
              Patient will be marked as skipped. Token number will remain #{patientToSkip?.queueNumber}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSkipModal(false)}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleConfirmSkip}>
            Skip Patient
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add to Queue Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header>
          <Modal.Title>Add Patient to Queue</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitQueue}>
          <Modal.Body>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Select from Appointments</Form.Label>
                  <Form.Select
                    name="appointmentId"
                    value={addFormData.appointmentId}
                    onChange={handleFormChange}
                  >
                    <option value="">-- No Appointment --</option>
                    {appointments.map(app => (
                      <option key={app.id} value={app.id}>
                        {formatTime(app.appointmentTime)} - {app.Patient?.name || 'Unknown'}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select an appointment or add patient directly
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Select Patient</Form.Label>
                  <Form.Select
                    name="patientId"
                    value={addFormData.patientId}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">-- Select Patient --</option>
                    {patients.map(patient => (
                      <option key={patient.id} value={patient.id}>
                        {patient.name} ({patient.age}yrs, {patient.gender})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Notes (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="notes"
                value={addFormData.notes}
                onChange={handleFormChange}
                placeholder="Add any notes about this patient's visit"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="emergency"
                name="isEmergency"
                label="Mark as Emergency (will be prioritized in queue)"
                checked={addFormData.isEmergency}
                onChange={handleFormChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add to Queue
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default PatientQueue;

